import { useState } from 'react';
import { Phone, Shield, X } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

export default function PhoneRequiredModal() {
  const { user, refreshUser } = useAuth();
  const [phone, setPhone] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  // Only show when logged-in non-admin user has no phone number
  if (!user || user.phone || user.role === 'admin') return null;

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!phone.trim()) { setError('Phone number is required.'); return; }
    setLoading(true);
    setError('');
    try {
      const res = await fetch(`${API}/users/phone`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ phone: phone.trim() }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Failed to save phone number.');
      await refreshUser();
    } catch (err) {
      setError(err.message);
      setLoading(false);
    }
  };

  return (
    <div className="fixed inset-0 z-[999] bg-[#2B2B2B]/60 backdrop-blur-sm flex items-center justify-center p-4" data-testid="phone-required-modal">
      <div className="bg-white rounded-3xl w-full shadow-2xl overflow-hidden" style={{ maxWidth: '420px' }}>
        {/* Header */}
        <div className="bg-[#2B2B2B] px-7 py-6">
          <div className="flex items-center gap-3 mb-2">
            <div className="w-10 h-10 rounded-full bg-[#C8A97E]/20 flex items-center justify-center flex-shrink-0">
              <Shield size={20} className="text-[#C8A97E]" />
            </div>
            <div>
              <h2 className="text-xl font-bold text-white" style={{ fontFamily: 'Playfair Display, serif' }}>
                One last step, {user.name?.split(' ')[0]}
              </h2>
              <p className="text-white/55 text-xs mt-0.5">Required for account security</p>
            </div>
          </div>
        </div>

        <div className="px-7 py-6">
          <div className="bg-[#C8A97E]/10 border border-[#C8A97E]/25 rounded-2xl p-4 mb-5">
            <p className="text-sm text-[#2B2B2B] leading-relaxed">
              <strong>Phone number is required</strong> to protect your account and prevent duplicate signups. One phone number can only be linked to one Perkly account.
            </p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-4">
            <div className="relative">
              <Phone size={16} className="absolute left-3.5 top-1/2 -translate-y-1/2 text-[#8A9A8B]" />
              <input
                type="tel"
                required
                value={phone}
                onChange={e => { setPhone(e.target.value); setError(''); }}
                placeholder="Your mobile number (e.g. 706-555-0100)"
                className="perkly-input pl-10"
                data-testid="phone-required-input"
                autoFocus
              />
            </div>

            {error && (
              <p className="text-xs text-red-500 bg-red-50 rounded-xl px-3 py-2" data-testid="phone-error">
                {error}
              </p>
            )}

            <button
              type="submit"
              disabled={loading || !phone.trim()}
              data-testid="phone-required-submit"
              className="w-full btn-primary justify-center py-3.5 disabled:opacity-60"
            >
              {loading
                ? <><div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin" /> Saving...</>
                : <><Phone size={15} /> Save Phone Number</>}
            </button>

            <p className="text-xs text-center text-[#2B2B2B]/45 leading-relaxed">
              Your number is private and used only for account security and deal alerts.
            </p>
          </form>
        </div>
      </div>
    </div>
  );
}
